import { useEffect, useRef, useState } from "react";
import { Mic, Square, Pause, Play, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import useLiveCaptions from "@/hooks/useLiveCaptions";
import LiveTranscription from "@/components/LiveTranscription";

function fmtTime(sec) {
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  const m = Math.floor((sec / 60) % 60).toString().padStart(2, "0");
  const h = Math.floor(sec / 3600).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function RecordingPanel({ meetingId, onUploaded }) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);
  const canvasRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const elapsedRef = useRef(0);
  // Live-caption ("pseudo-streaming") state
  const liveRecorderRef = useRef(null);
  const liveTickerRef = useRef(null);
  const liveBusyRef = useRef(false);

  const [phase, setPhase] = useState("idle"); // idle | recording | paused | stopped | uploading
  const [elapsed, setElapsed] = useState(0);
  const [consent, setConsent] = useState(false);
  // Live captions + agenda-drift alert arrive via WebSocket so anyone viewing
  // the meeting sees them too. The agenda alert is only rendered for the
  // organizer (they're the one driving the conversation).
  const { captions, alert: agendaAlert } = useLiveCaptions(meetingId, phase === "recording" || phase === "paused", 60);
  const [error, setError] = useState("");
  const [blobUrl, setBlobUrl] = useState("");

  // Drive the elapsed timer via an effect so React Compiler can track it.
  useEffect(() => {
    if (phase !== "recording") return undefined;
    const startAt = Date.now();
    const base = elapsedRef.current;
    const id = setInterval(() => {
      setElapsed(base + (Date.now() - startAt) / 1000);
    }, 250);
    timerRef.current = id;
    return () => {
      clearInterval(id);
      elapsedRef.current = base + (Date.now() - startAt) / 1000;
    };
  }, [phase]);

  // Drive waveform animation via an effect.
  useEffect(() => {
    if (phase !== "recording" || !analyserRef.current || !canvasRef.current) {
      return undefined;
    }
    let rafId;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const barCount = 48;
      const slice = Math.floor(bufferLength / barCount);
      const barW = canvas.width / barCount;
      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (let j = 0; j < slice; j++) sum += dataArray[i * slice + j];
        const v = sum / slice;
        const h = Math.max(2, (v / 255) * canvas.height);
        ctx.fillStyle = "#E53E3E";
        ctx.fillRect(i * barW + 1, (canvas.height - h) / 2, barW - 2, h);
      }
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, [phase]);

  // Unmount cleanup of media stream / recorder.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (liveTickerRef.current) clearInterval(liveTickerRef.current);
      try { mediaRecorderRef.current?.stop(); } catch (_e) { /* noop */ }
      try { liveRecorderRef.current?.stop(); } catch (_e) { /* noop */ }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      try { audioContextRef.current?.close(); } catch (_e) { /* noop */ }
    };
  }, []);

  async function start() {
    setError("");
    if (!consent) {
      setError("Please confirm participant recording consent first.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      streamRef.current = stream;

      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      audioContextRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        if (!blob.size) {
          setError("No audio captured.");
          setPhase("stopped");
          return;
        }
        // Auto-upload as soon as recording stops. No second click needed.
        setPhase("uploading");
        const form = new FormData();
        form.append("file", blob, `${meetingId}.webm`);
        form.append("duration_seconds", String(elapsedRef.current || 0));
        try {
          const { data } = await api.post(`/recordings/${meetingId}/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Recording uploaded. AI is processing the meeting in the background…");
          onUploaded?.(data);
        } catch (e) {
          setError(formatApiError(e));
          setPhase("stopped");
        }
      };
      mediaRecorderRef.current = rec;
      rec.start(1000);
      elapsedRef.current = 0;
      setElapsed(0);
      setPhase("recording");
      // Mark meeting as started
      try { await api.post(`/meetings/${meetingId}/start`); } catch (_e) { /* noop */ }

      // Start the secondary recorder for live (pseudo-streaming) captions.
      startLiveCaptions(stream, mime);
    } catch (e) {
      console.error(e);
      setError("Microphone access denied or unavailable. Please grant permission and try again.");
    }
  }

  function startLiveCaptions(stream, mime) {
    const SEG_MS = 15000; // send a chunk every 15 seconds
    const startSegment = () => {
      try {
        const lr = new MediaRecorder(stream, { mimeType: mime });
        const segChunks = [];
        lr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) segChunks.push(e.data);
        };
        lr.onstop = async () => {
          if (liveBusyRef.current) return;
          const blob = new Blob(segChunks, { type: "audio/webm" });
          if (blob.size < 5000) return;
          liveBusyRef.current = true;
          try {
            const form = new FormData();
            form.append("file", blob, "live.webm");
            form.append("meeting_id", meetingId);
            // The backend transcribes and broadcasts via WebSocket; we don't
            // touch local state — the WS subscriber renders the result.
            await api.post("/transcribe/stream-chunk", form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (_e) {
            /* live captions are best-effort; ignore */
          } finally {
            liveBusyRef.current = false;
          }
        };
        lr.start();
        liveRecorderRef.current = lr;
      } catch (e) {
        // Live captions are best-effort.
      }
    };
    startSegment();
    liveTickerRef.current = setInterval(() => {
      const cur = liveRecorderRef.current;
      try { cur && cur.state !== "inactive" && cur.stop(); } catch (_e) { /* noop */ }
      // Small delay so onstop fires + container finalizes before next segment.
      setTimeout(startSegment, 120);
    }, SEG_MS);
  }

  function stopLiveCaptions() {
    if (liveTickerRef.current) {
      clearInterval(liveTickerRef.current);
      liveTickerRef.current = null;
    }
    try {
      const cur = liveRecorderRef.current;
      if (cur && cur.state !== "inactive") cur.stop();
    } catch (_e) { /* noop */ }
    liveRecorderRef.current = null;
  }

  function pause() {
    mediaRecorderRef.current?.pause();
    setPhase("paused");
  }
  function resume() {
    mediaRecorderRef.current?.resume();
    setPhase("recording");
  }
  function stop() {
    try { mediaRecorderRef.current?.stop(); } catch (_e) { /* noop */ }
    stopLiveCaptions();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setPhase("stopped");
  }

  async function upload() {
    // Manual retry fallback if onstop's auto-upload fails (surfaced as a
    // Retry button when phase==='stopped' && error). Recordings auto-upload
    // on stop in the happy path.
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (!blob.size) {
      setError("No audio captured.");
      return;
    }
    setError("");
    setPhase("uploading");
    const form = new FormData();
    form.append("file", blob, `${meetingId}.webm`);
    form.append("duration_seconds", String(elapsed));
    try {
      const { data } = await api.post(`/recordings/${meetingId}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Recording uploaded. AI is processing the meeting...");
      onUploaded?.(data);
    } catch (e) {
      setError(formatApiError(e));
      setPhase("stopped");
    }
  }

  const isRec = phase === "recording";
  const isPaused = phase === "paused";

  return (
    <div className="border border-foreground p-6 bg-background">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">
            Live recording
          </div>
          <div
            data-testid={TEST_IDS.recordingTimer}
            className="text-5xl font-black font-mono tracking-tight"
          >
            {fmtTime(elapsed)}
          </div>
          <div className="text-xs text-muted-foreground">
            Audio · Opus / WebM · Chunked transcription supports meetings up to 3 hours
          </div>
        </div>

        <div className="flex items-center gap-3">
          {phase === "idle" && (
            <Button
              data-testid={TEST_IDS.recordStartBtn}
              onClick={start}
              size="lg"
              className="rounded-full h-14 px-7 bg-primary hover:bg-[hsl(var(--primary-hover))] text-primary-foreground shadow-lift"
            >
              <Mic className="w-5 h-5 mr-2" />
              Start recording
            </Button>
          )}
          {isRec && (
            <>
              <Button
                data-testid={TEST_IDS.recordPauseBtn}
                onClick={pause}
                variant="outline"
                size="lg"
                className="rounded-none h-14"
              >
                <Pause className="w-5 h-5 mr-2" /> Pause
              </Button>
              <Button
                data-testid={TEST_IDS.recordStopBtn}
                onClick={stop}
                size="lg"
                className="rounded-none h-14 bg-foreground text-background"
              >
                <Square className="w-5 h-5 mr-2" /> End meeting
              </Button>
            </>
          )}
          {isPaused && (
            <>
              <Button
                onClick={resume}
                size="lg"
                className="rounded-none h-14 bg-red-600 hover:bg-red-700 text-white"
                data-testid="record-resume-btn"
              >
                <Play className="w-5 h-5 mr-2" /> Resume
              </Button>
              <Button
                data-testid={TEST_IDS.recordStopBtn}
                onClick={stop}
                size="lg"
                variant="outline"
                className="rounded-none h-14"
              >
                <Square className="w-5 h-5 mr-2" /> End
              </Button>
            </>
          )}
          {phase === "stopped" && error && (
            <Button
              data-testid="retry-upload-btn"
              onClick={upload}
              size="lg"
              className="rounded-none h-14 bg-foreground text-background"
            >
              Retry upload
            </Button>
          )}
          {phase === "stopped" && !error && (
            <div
              data-testid="finalizing-meeting"
              className="h-14 px-6 border border-foreground flex items-center text-sm font-semibold uppercase tracking-[0.2em]"
            >
              Finalizing meeting…
            </div>
          )}
          {phase === "uploading" && (
            <div className="h-14 px-6 border border-foreground flex items-center text-sm font-semibold uppercase tracking-[0.2em]">
              Uploading…
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <canvas
          ref={canvasRef}
          width={1200}
          height={80}
          className="w-full h-20 rounded-2xl border border-border bg-secondary/50"
        />
      </div>

      {(phase === "recording" || phase === "paused" || captions.length > 0) && (
        <LiveTranscription
          captions={captions}
          agendaAlert={agendaAlert}
          live={phase === "recording"}
          elapsed={elapsed}
        />
      )}

      {phase === "stopped" && blobUrl && (
        <div className="mt-4 rounded-2xl bg-card border border-border p-4">
          <div className="eyebrow mb-2">
            Local preview (already uploading)
          </div>
          <audio src={blobUrl} controls className="w-full" />
        </div>
      )}

      <div className="mt-6 border-t border-border pt-4">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            data-testid={TEST_IDS.consentCheckbox}
            checked={consent}
            onCheckedChange={(v) => setConsent(!!v)}
            className="rounded-none mt-0.5"
          />
          <span className="text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Recording consent.</strong> This meeting will be
            recorded and transcribed using AI. All participants acknowledge that audio may be
            processed for transcription, meeting summary, decisions, and task generation.
          </span>
        </label>
      </div>

      {error && (
        <div className="mt-4 border border-red-500 bg-red-50 text-red-800 p-3 flex items-start gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}
